import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/MapExplorer.css';

interface MapLocation {
  location_id: string;
  location_name: string;
  location_type: 'municipality' | 'county';
  weight_kg: number;
  value_nok: number;
  vessel_count: number;
  kommunenummer?: string;
}

interface GeoJSONFeature {
  type: 'Feature';
  properties: {
    name: string;
    kommunenummer: string;
    id?: string;
  };
  geometry: {
    type: 'Polygon' | 'Point';
    coordinates: any[];
  };
}

interface MapExplorerProps {
  daysBack: number;
  onDaysBackChange: (days: number) => void;
  aggregation?: string;
  onAggregationChange?: (aggregation: string) => void;
}

const MapExplorer: React.FC<MapExplorerProps> = ({
  daysBack,
  onDaysBackChange,
  aggregation = 'municipality',
  onAggregationChange,
}) => {
  const navigate = useNavigate();
  const [mapData, setMapData] = useState<MapLocation[]>([]);
  const [geoJsonData, setGeoJsonData] = useState<GeoJSONFeature[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load GeoJSON on mount
  useEffect(() => {
    const loadGeoJSON = async () => {
      try {
        const response = await fetch('/data/norway-municipalities.json');
        if (!response.ok) {
          throw new Error('Kunne ikke laste kommunedata');
        }
        const data = await response.json();
        setGeoJsonData(data.features || []);
      } catch (err) {
        setError(`Feil ved lasting av GeoJSON: ${err instanceof Error ? err.message : String(err)}`);
      }
    };

    loadGeoJSON();
  }, []);

  // Fetch map data when daysBack or aggregation changes
  useEffect(() => {
    const fetchMapData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `/api/map/locations?aggregation=${aggregation}&days_back=${daysBack}`
        );
        if (!response.ok) {
          throw new Error('Kunne ikke hente kartdata');
        }
        const data = await response.json();
        setMapData(data.features || []);
      } catch (err) {
        setError(`Feil ved lasting av kartdata: ${err instanceof Error ? err.message : String(err)}`);
        setMapData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMapData();
  }, [daysBack, aggregation]);

  // Calculate centroid from geometry
  const getCentroid = (geometry: any): [number, number] | null => {
    if (geometry.type === 'Point') {
      const [lon, lat] = geometry.coordinates;
      return [lat, lon];
    } else if (geometry.type === 'Polygon') {
      const coords = geometry.coordinates[0];
      let lat = 0,
        lon = 0;
      coords.forEach((coord: any) => {
        lon += coord[0];
        lat += coord[1];
      });
      return [lat / coords.length, lon / coords.length];
    }
    return null;
  };

  // Create color based on weight percentile
  const getMarkerColor = (weight: number, minWeight: number, maxWeight: number) => {
    if (maxWeight === minWeight) return '#87CEEB';
    const percentile = (weight - minWeight) / (maxWeight - minWeight);
    // Light blue to dark blue gradient
    const hue = 215; // Blue hue
    const lightness = 80 - percentile * 40; // 80% to 40%
    return `hsl(${hue}, 100%, ${lightness}%)`;
  };

  // Join GeoJSON with map data
  const getMarkerRadius = (weight: number) => {
    return (Math.sqrt(weight / 1000) + 5) * 0.35;
  };

  // Get min/max for color scaling
  const weights = mapData.map((loc) => loc.weight_kg || 0);
  const minWeight = weights.length > 0 ? Math.min(...weights) : 0;
  const maxWeight = weights.length > 0 ? Math.max(...weights) : 1;

  // Create markers by joining GeoJSON with map data
  const markers = geoJsonData
    .map((feature) => {
      // Match by kommune code (numeric identifier)
      const matchingData = mapData.find(
        (loc) => loc.location_id === feature.properties.kommunenummer
      );

      if (!matchingData) return null;

      const centroid = getCentroid(feature.geometry);
      if (!centroid) return null;

      const weight = matchingData.weight_kg || 0;
      return {
        position: centroid,
        data: matchingData,
        color: getMarkerColor(weight, minWeight, maxWeight),
        radius: getMarkerRadius(weight),
      };
    })
    .filter((marker) => marker !== null);

  return (
    <div className="map-explorer-container">
      {error && <div className="map-error">{error}</div>}
      {loading && <div className="map-loading">Laster kartdata...</div>}

      <div className="map-wrapper">
        <MapContainer
          center={[60.5, 8.0]}
          zoom={5}
          style={{ height: '100%', width: '100%' }}
          className="leaflet-map"
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            maxZoom={18}
          />

          {markers.map((marker, idx) => (
            <CircleMarker
              key={idx}
              center={marker.position}
              radius={marker.radius}
              fillColor={marker.color}
              fillOpacity={0.7}
              stroke={true}
              color="#333"
              weight={1}
              opacity={0.8}
              eventHandlers={{
                click: () => {
                  navigate(`/location/${marker.data.location_id}`, {
                    state: {
                      location_name: marker.data.location_name,
                    },
                  });
                },
              }}
            >
              <Tooltip>{marker.data.location_name} - {Math.round((marker.data.weight_kg || 0) / 1000).toLocaleString('en-SE')} tonn</Tooltip>
              <Popup>
                <div className="marker-popup">
                  <div className="popup-name">{marker.data.location_name}</div>
                  <div className="popup-stat">
                    <strong>{(marker.data.weight_kg || 0).toLocaleString()} kg</strong>
                  </div>
                  <div className="popup-stat">
                    {marker.data.vessel_count} vessels
                  </div>
                  <div className="popup-stat">
                    {((marker.data.value_nok || 0) / 1000000).toFixed(1)}M NOK
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>

      <div className="map-controls">
        <div className="time-range-buttons">
          {[2, 7, 30].map((days) => (
            <button
              key={days}
              className={`time-btn ${daysBack === days ? 'active' : ''}`}
              onClick={() => onDaysBackChange(days)}
            >
              Siste {days} dager
            </button>
          ))}
        </div>
      </div>

      <div className="map-legend">
        <div className="legend-item">
          <span className="legend-text">Sirkelstørrelse = total fangstvekt (kg)</span>
        </div>
        <div className="legend-item">
          <span className="legend-text">Farge: Lyseblå (liten) → Mørkeblå (stor)</span>
        </div>
        <div className="legend-item">
          <span className="legend-text">Klikk markør for detaljert stedsanalyse</span>
        </div>
      </div>
    </div>
  );
};

export default MapExplorer;
